import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import { Languages } from '@s4l/i18n/types/Languages'
import { countryDataSource } from '../../_datasource/CountryDatasource'
import type { ALLOWED_MARKETS } from './Market'

export const LanguageValidation = z.object({
  value: z.enum(Languages, { required_error: '[Language] value is required' }),
})

export const FROM_MARKET_TO_LANGUAGE = Object.values(countryDataSource).reduce(
  (acc, config) => {
    if (!acc[config.market]) {
      acc[config.market] = config.language
    }
    return acc
  },
  {} as Record<(typeof ALLOWED_MARKETS)[number], Languages | undefined>
)

const TRUSTPILOT_SUPPORTED_LANGUAGES = new Set<Languages>([
  'en',
  'de',
  'es',
  'fr',
  'it',
  'da',
  'nl',
  'fi',
  'pl',
  'pt',
  'ar',
])

export class Language extends Model {
  static create({ value }: z.infer<typeof LanguageValidation>) {
    LanguageValidation.parse({ value })
    return new Language(value)
  }

  static createOrEmpty({ value }: z.infer<typeof LanguageValidation>) {
    try {
      return Language.create({ value })
    } catch {
      return Language.empty()
    }
  }

  static empty() {
    return new Language()
  }

  static default() {
    return Language.create({ value: 'en' })
  }

  static fromMarket(market?: (typeof ALLOWED_MARKETS)[number]) {
    if (!market) return Language.empty()
    const language = FROM_MARKET_TO_LANGUAGE[market]
    if (!language) return Language.empty()
    return Language.create({ value: language })
  }

  static fromJSON(json: ReturnType<Language['toJSON']>) {
    if (!json.value) {
      return Language.empty()
    }
    return Language.create({ value: json.value })
  }

  constructor(private readonly _value?: Languages) {
    super()
  }

  get value() { return this._value } // prettier-ignore

  isEmpty() { return this._value === undefined } // prettier-ignore

  isSupportedByTrustPilot(): boolean {
    if (!this._value) return false
    return TRUSTPILOT_SUPPORTED_LANGUAGES.has(this._value)
  }

  toJSON() {
    return { value: this.value ?? null }
  }
}
